import { isShopperAgentPhoneProvisioningEnabled } from "../../provider/feature-flag";
import type {
  ProductSetupConfig,
  SetupTask,
  SetupTaskGroup,
} from "../../registry/types";
import { ConciergeIntakeExperience } from "./concierge-intake-experience";
import {
  CONCIERGE_INTAKE_HERO_IMAGE_URL,
  CONFIGURE_PLAYBOOK_TASK_ID,
  SENDING_NUMBER_TASK_ID,
} from "./constants";
import { SendingNumberStatusModal } from "./sending-number-status-modal";

const SENDING_NUMBER_TASK: SetupTask = {
  id: SENDING_NUMBER_TASK_ID,
  title: "Approving your sending number",
  description:
    "We'll keep checking the number that Concierge uses to text customers. This usually takes a little time after setup.",
  visiblePredicate: isShopperAgentPhoneProvisioningEnabled,
  route: (teamId) => `/stores/${teamId}/redo-concierge`,
  completionPredicate: (_team, progress) =>
    progress.intakeAnswers.phoneProvisioningStatus === "approved",
  requiredForLive: true,
  contextualModal: {
    title: "Sending number status",
    ctaText: "Close",
    renderBody: SendingNumberStatusModal,
  },
};

const CONFIGURE_PLAYBOOK_TASK: SetupTask = {
  id: CONFIGURE_PLAYBOOK_TASK_ID,
  title: "Review your Concierge playbook",
  description:
    "See where Concierge reaches out, how it speaks, and what it can handle for shoppers.",
  route: (teamId) => `/stores/${teamId}/redo-concierge/playbook`,
  completionPredicate: () => false,
  requiredForLive: true,
  completeOnRoute: /\/redo-concierge\/(playbook|settings)(\/|$)/,
};

const TASK_LIST = [SENDING_NUMBER_TASK, CONFIGURE_PLAYBOOK_TASK];

const GROUPS: SetupTaskGroup[] = [
  { id: "setup", title: "Setup", taskIds: TASK_LIST.map((task) => task.id) },
];

export const shopperAgentProductConfig: ProductSetupConfig = {
  product: "shopperAgent",
  displayName: "Concierge",
  routePattern: /\/redo-concierge(\/|$)/,
  resumeRoute: (storeId) => `/stores/${storeId}/redo-concierge`,
  // No `onIntakeSubmit` yet — Concierge has no post-intake activation
  // step today. Intake just persists answers; the welcome overlay does
  // not fire (matches existing host behavior pre-refactor).
  intake: {
    questions: [],
    welcome: {
      title: "Welcome to Concierge",
      subtitle: "Let's get your Concierge live.",
    },
    dismissible: false,
    heroImageUrl: CONCIERGE_INTAKE_HERO_IMAGE_URL,
    renderExperience: ConciergeIntakeExperience,
    showProgress: true,
  },
  groups: GROUPS,
  tasks: Object.fromEntries(TASK_LIST.map((task) => [task.id, task])),
  asyncTaskStatus: {
    pollIntervalMs: 30_000,
    load: async ({ rpcClient, progress, team }) => {
      if (!isShopperAgentPhoneProvisioningEnabled(team)) {
        return {};
      }
      const readiness = await rpcClient.getConciergePhoneReadiness({});
      const hasIMessage =
        team.settings?.shopperAgentAdmin?.allowIMessage === true;
      if (readiness.provisioningStatus === "approved") {
        return {
          [SENDING_NUMBER_TASK.id]: {
            complete: true,
            description:
              "Your sending number is approved and Concierge can message customers.",
            variant: "complete",
          },
        };
      }
      if (readiness.provisioningStatus === "rejected") {
        return {
          [SENDING_NUMBER_TASK.id]: {
            complete: false,
            description:
              readiness.phoneNumberLastError ??
              "Your sending number needs attention before Concierge can go live.",
            variant: "needs_attention",
          },
        };
      }
      if (
        readiness.provisioningStatus === "pending" ||
        progress.intakeAnswers.phoneProvisioningStatus === "pending"
      ) {
        return {
          [SENDING_NUMBER_TASK.id]: {
            complete: false,
            description: hasIMessage
              ? "Concierge can message iPhone shoppers now. Your SMS number is being reviewed so it can reach everyone else too."
              : "Your SMS sending number is being reviewed. Concierge will go live automatically once it is approved.",
            variant: "pending",
          },
        };
      }
      return {
        [SENDING_NUMBER_TASK.id]: {
          complete: false,
          description:
            "Finish the sending-number step so Concierge can text customers.",
          variant: "not_started",
        },
      };
    },
  },
  completion: {
    title: "Concierge is ready",
    body: "Concierge is ready to keep building customer relationships.",
    ctaText: "Got it",
  },
  // Keep the memo key scoped to the settings + flag state this product reads.
  relevantTeamSlice: (team) => ({
    shopperAgent: team.settings?.shopperAgent,
    shopperAgentAdmin: {
      allowIMessage: team.settings?.shopperAgentAdmin?.allowIMessage,
    },
    phoneProvisioningEnabled: isShopperAgentPhoneProvisioningEnabled(team),
  }),
};
